import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, newId } from '../api.js';
import { ErrorBox, Spinner } from '../components.jsx';
import { deleteDraft, draftKey, loadDraft, saveDraft } from '../lib/drafts.js';
import { enqueue } from '../lib/outbox.js';
import {
  COLORS, LABEL_SIZES, SRC_MIME, WIDTHS, cleanDoc, drawDoc, exportImage, isBlank, labelAt, newDoc,
  prepareBackground, randomHex, strokeHit,
} from '../lib/sketch.js';
import { decodeImage } from '../lib/media.js';
import { useJob } from '../lib/useJob.js';

const snap = (doc) => JSON.stringify({ strokes: doc.strokes, labels: doc.labels });
const UNITS = ['mm', 'm', 'm²'];

function Editor({ job, editSkid }) {
  const nav = useNavigate();
  const key = draftKey(job.id, editSkid);

  const docRef = useRef(null);
  const bgRef = useRef(null); // ImageBitmap drawn under the strokes
  const bgBlobRef = useRef(null); // the JPEG that gets saved with the sketch
  const hist = useRef({ past: [], future: [] });
  const cur = useRef(null); // stroke being drawn
  const gesture = useRef(null);
  const canvasRef = useRef(null);
  const areaRef = useRef(null);
  const raf = useRef(0);
  const draftTimer = useRef(0);
  const finished = useRef(false); // saved or discarded: stop writing drafts
  const photoCam = useRef(null);
  const photoPick = useRef(null);

  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [, setRev] = useState(0);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [wIdx, setWIdx] = useState(1);
  const [area, setArea] = useState({ w: 0, h: 0 });
  const [label, setLabel] = useState(null); // { label?, x, y, text, before }
  const [penSeen, setPenSeen] = useState(false);
  const [fingers, setFingers] = useState(false);
  const [restored, setRestored] = useState(false);
  const [photoMenu, setPhotoMenu] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Native pointer handlers read these, not stale closures.
  const t = useRef({});
  t.current = { tool, color, width: WIDTHS[wIdx], fingers };
  const penRef = useRef(false);

  const doc = docRef.current;

  // ---- drawing ----------------------------------------------------------------------------------
  const redraw = useCallback(() => {
    const c = canvasRef.current;
    const d = docRef.current;
    if (!c || !d) return;
    const ctx = c.getContext('2d');
    const s = c.width / d.w;
    ctx.setTransform(s, 0, 0, s, 0, 0);
    drawDoc(ctx, d, bgRef.current, cur.current);
  }, []);
  const schedule = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(() => { raf.current = 0; redraw(); });
  }, [redraw]);

  // ---- drafts (kept on the device until the sketch is saved) -----------------------------------------
  const flushDraft = useCallback(() => {
    clearTimeout(draftTimer.current);
    const d = docRef.current;
    if (!d || finished.current) return;
    if (isBlank(d) && !bgBlobRef.current) { deleteDraft(key); return; }
    saveDraft({ key, jobId: job.id, parent: editSkid || null, doc: { ...d }, bg: bgBlobRef.current });
  }, [key, job.id, editSkid]);
  const queueDraft = useCallback(() => {
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(flushDraft, 300);
  }, [flushDraft]);

  const changed = useCallback(() => { setRev((r) => r + 1); schedule(); queueDraft(); }, [schedule, queueDraft]);

  const commit = useCallback((before) => {
    if (before === snap(docRef.current)) return;
    hist.current.past.push(before);
    if (hist.current.past.length > 100) hist.current.past.shift();
    hist.current.future = [];
    changed();
  }, [changed]);

  const applySnap = (s) => { const o = JSON.parse(s); docRef.current.strokes = o.strokes; docRef.current.labels = o.labels; };
  function undo() {
    const h = hist.current;
    if (!h.past.length) return;
    h.future.push(snap(docRef.current));
    applySnap(h.past.pop());
    changed();
  }
  function redo() {
    const h = hist.current;
    if (!h.future.length) return;
    h.past.push(snap(docRef.current));
    applySnap(h.future.pop());
    changed();
  }

  // ---- load: a saved draft wins; else the sketch being edited; else a fresh sheet -----------------------
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const draft = await loadDraft(key);
        const draftDoc = draft && cleanDoc(draft.doc);
        if (draftDoc && (!isBlank(draftDoc) || draft.bg)) {
          docRef.current = draftDoc;
          if (draft.bg) { bgBlobRef.current = draft.bg; bgRef.current = await decodeImage(draft.bg, false); draftDoc.hasBg = true; }
          setRestored(true);
        } else if (editSkid) {
          const d = cleanDoc(await api.get(`/api/jobs/${job.id}/sketches/${editSkid}`));
          if (d.hasBg) {
            const r = await fetch(`/api/jobs/${job.id}/sketches/${editSkid}/bg`, { credentials: 'include' });
            if (r.ok) { bgBlobRef.current = await r.blob(); bgRef.current = await decodeImage(bgBlobRef.current, false); } else d.hasBg = false;
          }
          docRef.current = d;
        } else {
          docRef.current = newDoc(window.innerHeight > window.innerWidth * 1.15);
        }
        if (live) setPhase('ready');
      } catch (e) {
        if (!live) return;
        setLoadError(e.status === 404 ? "This sketch can't be opened for editing (its source data isn't saved)." : "Couldn't open the sketch. Check your signal and try again.");
        setPhase('error');
      }
    })();
    return () => { live = false; };
  }, [key, editSkid, job.id]);

  // Flush the draft whenever the page is hidden/closed, and on leaving this screen.
  useEffect(() => {
    const hide = () => document.visibilityState === 'hidden' && flushDraft();
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', flushDraft);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('pagehide', flushDraft);
      document.body.style.overflow = prev;
      cancelAnimationFrame(raf.current);
      flushDraft();
    };
  }, [flushDraft]);

  // ---- layout: fit the sheet inside the available area ---------------------------------------------------
  useLayoutEffect(() => {
    if (phase !== 'ready' || !areaRef.current) return undefined;
    const el = areaRef.current;
    const measure = () => setArea({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase]);

  // A blank new sheet follows the device's orientation (so rotating an iPad before drawing gives a
  // landscape page). Once there's a drawing or photo, or when re-editing, the shape is fixed.
  useEffect(() => {
    const d = docRef.current;
    if (phase !== 'ready' || !d || editSkid || !area.w || !isBlank(d) || d.hasBg) return;
    if (d.h > d.w !== area.h > area.w) {
      [d.w, d.h] = [d.h, d.w];
      setRev((r) => r + 1);
      schedule();
    }
  }, [phase, area.w, area.h, editSkid, schedule]);

  let cssW = 0;
  let cssH = 0;
  if (doc && area.w) {
    const s = Math.min((area.w - 16) / doc.w, (area.h - 16) / doc.h);
    cssW = Math.max(50, Math.floor(doc.w * s));
    cssH = Math.max(50, Math.floor(doc.h * s));
  }
  useLayoutEffect(() => {
    const c = canvasRef.current;
    if (!c || !cssW) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const w = Math.round(cssW * dpr);
    const h = Math.round((w * doc.h) / doc.w);
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    redraw();
  });

  // ---- pointer input (finger, mouse, Apple Pencil) ---------------------------------------------------------
  useEffect(() => {
    const c = canvasRef.current;
    if (phase !== 'ready' || !c) return undefined;

    const toDoc = (e) => {
      const r = c.getBoundingClientRect();
      const d = docRef.current;
      return [((e.clientX - r.left) * d.w) / r.width, ((e.clientY - r.top) * d.h) / r.height];
    };
    const erase = (pt, g) => {
      const d = docRef.current;
      const n = d.strokes.length + d.labels.length;
      d.strokes = d.strokes.filter((s) => !strokeHit(s, pt, 22));
      d.labels = d.labels.filter((l) => !labelAt({ labels: [l] }, pt));
      if (d.strokes.length + d.labels.length !== n) g.changed = true;
    };

    const down = (e) => {
      const d = docRef.current;
      if (!d || gesture.current) return; // one pointer at a time: ignores a second finger or a palm
      if (e.pointerType === 'pen' && !penRef.current) { penRef.current = true; setPenSeen(true); }
      // Once a Pencil has been used, fingers don't draw (palm rejection) unless switched back on.
      if (e.pointerType === 'touch' && penRef.current && !t.current.fingers) return;
      e.preventDefault();
      c.setPointerCapture(e.pointerId);
      const pt = toDoc(e);
      const before = snap(d);
      const { tool: tl, color: col, width } = t.current;
      if (tl === 'pen') {
        cur.current = { color: col, width, pts: [pt] };
        gesture.current = { id: e.pointerId, type: 'pen', before };
      } else if (tl === 'eraser') {
        gesture.current = { id: e.pointerId, type: 'erase', before, changed: false, last: pt };
        erase(pt, gesture.current);
      } else {
        const hit = labelAt(d, pt);
        gesture.current = hit
          ? { id: e.pointerId, type: 'move', label: hit, start: pt, orig: { x: hit.x, y: hit.y }, moved: false, before }
          : { id: e.pointerId, type: 'add', pt, start: pt, cancelled: false, before };
      }
      schedule();
    };

    const move = (e) => {
      const g = gesture.current;
      if (!g || e.pointerId !== g.id) return;
      e.preventDefault();
      const events = e.getCoalescedEvents?.();
      for (const ev of events?.length ? events : [e]) {
        const pt = toDoc(ev);
        if (g.type === 'pen') {
          const last = cur.current.pts[cur.current.pts.length - 1];
          if (Math.hypot(pt[0] - last[0], pt[1] - last[1]) >= 1.5) cur.current.pts.push([Math.round(pt[0] * 10) / 10, Math.round(pt[1] * 10) / 10]);
        } else if (g.type === 'erase') {
          // Sweep along the path: a fast flick can jump over a thin line between two samples.
          const steps = Math.max(1, Math.ceil(Math.hypot(pt[0] - g.last[0], pt[1] - g.last[1]) / 10));
          for (let i = 1; i <= steps; i++) erase([g.last[0] + ((pt[0] - g.last[0]) * i) / steps, g.last[1] + ((pt[1] - g.last[1]) * i) / steps], g);
          g.last = pt;
        } else if (g.type === 'move') {
          if (!g.moved && Math.hypot(pt[0] - g.start[0], pt[1] - g.start[1]) > 10) g.moved = true;
          if (g.moved) { g.label.x = g.orig.x + pt[0] - g.start[0]; g.label.y = g.orig.y + pt[1] - g.start[1]; }
        } else if (g.type === 'add' && Math.hypot(pt[0] - g.start[0], pt[1] - g.start[1]) > 25) {
          g.cancelled = true; // a drag on empty space isn't a tap: don't place a label
        }
      }
      schedule();
    };

    const up = (e) => {
      const g = gesture.current;
      if (!g || e.pointerId !== g.id) return;
      gesture.current = null;
      const d = docRef.current;
      if (g.type === 'pen') {
        if (cur.current) { d.strokes.push(cur.current); cur.current = null; }
        commit(g.before);
      } else if (g.type === 'erase') {
        if (g.changed) commit(g.before);
      } else if (g.type === 'move') {
        if (g.moved) commit(g.before);
        else setLabel({ label: g.label, x: g.label.x, y: g.label.y, text: g.label.text, before: g.before });
      } else if (g.type === 'add' && !g.cancelled && e.type !== 'pointercancel') {
        setLabel({ label: null, x: g.pt[0], y: g.pt[1], text: '', before: g.before });
      }
      schedule();
    };

    c.addEventListener('pointerdown', down);
    c.addEventListener('pointermove', move);
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    return () => {
      c.removeEventListener('pointerdown', down);
      c.removeEventListener('pointermove', move);
      c.removeEventListener('pointerup', up);
      c.removeEventListener('pointercancel', up);
    };
  }, [phase, schedule, commit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- labels ---------------------------------------------------------------------------------------------
  function finishLabel(remove) {
    const d = docRef.current;
    const text = label.text.trim();
    if (label.label) {
      if (remove || !text) d.labels = d.labels.filter((l) => l !== label.label);
      else label.label.text = text;
    } else if (text && !remove) {
      d.labels.push({ x: label.x, y: label.y, text, color: t.current.color, size: LABEL_SIZES[wIdx] });
    }
    commit(label.before);
    setLabel(null);
    schedule();
  }

  // ---- background photo ---------------------------------------------------------------------------------
  async function addPhoto(file) {
    setPhotoMenu(false);
    if (!file) return;
    try {
      const { blob, bitmap } = await prepareBackground(file);
      const d = docRef.current;
      bgRef.current?.close?.();
      bgRef.current = bitmap;
      bgBlobRef.current = blob;
      d.hasBg = true;
      if (isBlank(d)) { // nothing drawn yet: shape the sheet to the photo
        const s = 1600 / Math.max(bitmap.width, bitmap.height);
        d.w = Math.round(bitmap.width * s);
        d.h = Math.round(bitmap.height * s);
      }
      setError(null);
      changed();
      flushDraft(); // a photo is the one thing that can't be redrawn: don't wait
    } catch (e) {
      console.warn('[sketch] background photo failed:', e?.name, e?.message);
      setError("Couldn't use that photo. Try another one.");
    }
  }
  function removePhoto() {
    bgRef.current?.close?.();
    bgRef.current = null;
    bgBlobRef.current = null;
    docRef.current.hasBg = false;
    setPhotoMenu(false);
    changed();
  }

  // ---- save: picture + (hidden) source data + background photo go through the upload queue ----------------------
  async function save() {
    setSaving(true);
    setError(null);
    try {
      const d = docRef.current;
      const skid = randomHex(8);
      const sid = newId();
      const { blob, mime } = await exportImage(d, bgRef.current);
      const base = { kind: 'file', jobId: job.id, jobName: job.name, sid };
      const items = [{ ...base, id: newId(), name: 'Sketch', blob, mime, query: `as=sketch&skid=${skid}` }];
      const source = JSON.stringify({ v: 1, w: d.w, h: d.h, hasBg: !!bgBlobRef.current, strokes: d.strokes, labels: d.labels, parent: editSkid || null });
      items.push({ ...base, id: newId(), name: 'Sketch data', quiet: true, blob: new Blob([source], { type: SRC_MIME }), mime: SRC_MIME, query: `as=sketch-source&skid=${skid}` });
      if (bgBlobRef.current) items.push({ ...base, id: newId(), name: 'Sketch photo', quiet: true, blob: bgBlobRef.current, mime: 'image/jpeg', query: `as=sketch-bg&skid=${skid}` });
      const n = items.length;
      items.push({ ...base, id: newId(), kind: 'complete', caption: caption.trim() });
      finished.current = true;
      clearTimeout(draftTimer.current);
      await enqueue(items);
      await deleteDraft(key);
      nav(`/job/${job.id}/done?what=files&sid=${sid}&n=${n}`, { replace: true });
    } catch (_) {
      finished.current = false;
      setSaving(false);
      setError("Couldn't save the sketch. Your drawing is still here – try again.");
    }
  }

  async function startOver() {
    if (!window.confirm('Throw away this unsaved sketch and start again?')) return;
    finished.current = true;
    await deleteDraft(key);
    window.location.reload();
  }

  // ---- render -------------------------------------------------------------------------------------------------
  if (phase === 'loading') return <div className="fixed inset-0 z-40 flex items-center justify-center bg-mist dark:bg-charcoal"><Spinner className="h-10 w-10" /></div>;
  if (phase === 'error') {
    return (
      <div className="fixed inset-0 z-40 space-y-4 bg-mist p-4 dark:bg-charcoal">
        <ErrorBox>{loadError}</ErrorBox>
        <Link to={`/job/${job.id}`} className="btn-ghost w-full">Back to the job</Link>
      </div>
    );
  }

  const blank = isBlank(doc) && !doc.hasBg;
  const h = hist.current;
  const tb = (active) =>
    `flex-1 rounded-xl border px-3 py-2.5 text-base font-medium ${active ? 'border-navy bg-navy text-white dark:border-steel dark:bg-steel' : 'border-steel/40 bg-white dark:bg-charcoal'}`;

  return createPortal(
    <div className="fixed inset-0 z-40 flex select-none flex-col bg-mist text-charcoal dark:bg-charcoal dark:text-mist">
      <input ref={photoCam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addPhoto(e.target.files[0]); e.target.value = ''; }} />
      <input ref={photoPick} type="file" accept="image/*" className="hidden" onChange={(e) => { addPhoto(e.target.files[0]); e.target.value = ''; }} />

      <header className="flex items-center gap-2 bg-navy px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] text-white dark:border-b dark:border-white/10 dark:bg-charcoal">
        <Link to={`/job/${job.id}`} className="rounded-lg px-2 py-2 text-base" aria-label="Close sketch">✕ Close</Link>
        <div className="min-w-0 flex-1 truncate text-center text-sm opacity-80">{job.jobNumber || job.name}</div>
        <button type="button" className="rounded-lg px-3 py-2 text-lg disabled:opacity-30" disabled={!h.past.length} onClick={undo} aria-label="Undo">↶</button>
        <button type="button" className="rounded-lg px-3 py-2 text-lg disabled:opacity-30" disabled={!h.future.length} onClick={redo} aria-label="Redo">↷</button>
        <button type="button" className="rounded-lg bg-amber px-4 py-2 text-base font-bold text-charcoal disabled:opacity-40" disabled={blank} onClick={() => setSaveOpen(true)}>Save</button>
      </header>

      {restored && (
        <div className="flex items-center justify-between gap-3 bg-amber/25 px-4 py-2 text-sm">
          <span>Restored your unsaved sketch.</span>
          <button type="button" className="underline" onClick={startOver}>Start over</button>
        </div>
      )}
      {error && <div className="px-3 pt-2"><ErrorBox>{error}</ErrorBox></div>}

      <div ref={areaRef} className="relative flex min-h-0 flex-1 items-center justify-center">
        <canvas
          ref={canvasRef}
          style={{ width: cssW, height: cssH, touchAction: 'none', WebkitTouchCallout: 'none', cursor: tool === 'label' ? 'text' : 'crosshair' }}
          className="rounded bg-white shadow-md ring-1 ring-black/10"
          onContextMenu={(e) => e.preventDefault()}
          aria-label="Sketch sheet"
        />
        {blank && (
          <p className="pointer-events-none absolute left-1/2 -translate-x-1/2 px-5 text-center text-steel" style={{ width: cssW }}>
            Draw with your finger or Apple Pencil.<br />Use <b>Label</b> and tap the sheet to add measurements like “4.2 m”.
          </p>
        )}
      </div>

      <footer className="space-y-2 border-t border-steel/20 bg-mist px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 dark:bg-charcoal">
        {photoMenu && (
          <div className="flex gap-2">
            <button type="button" className={tb(false)} onClick={() => photoCam.current.click()}>Take photo</button>
            <button type="button" className={tb(false)} onClick={() => photoPick.current.click()}>Choose photo</button>
            {doc.hasBg && <button type="button" className={`${tb(false)} !text-ascred`} onClick={removePhoto}>Remove photo</button>}
          </div>
        )}
        <div className="flex gap-2">
          <button type="button" className={tb(tool === 'pen')} onClick={() => setTool('pen')}>Pen</button>
          <button type="button" className={tb(tool === 'eraser')} onClick={() => setTool('eraser')}>Eraser</button>
          <button type="button" className={tb(tool === 'label')} onClick={() => setTool('label')}>Label</button>
          <button type="button" className={tb(photoMenu || doc.hasBg)} onClick={() => setPhotoMenu(!photoMenu)}>Photo</button>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {COLORS.map((c) => (
              <button
                key={c} type="button" aria-label={`Colour ${c}`}
                className={`h-9 w-9 rounded-full border-2 ${color === c ? 'border-amber ring-2 ring-navy dark:ring-white' : 'border-white/60'}`}
                style={{ background: c }} onClick={() => setColor(c)}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {WIDTHS.map((w, i) => (
              <button
                key={w} type="button" aria-label={`Size ${i + 1}`}
                className={`flex h-10 w-10 items-center justify-center rounded-lg border ${wIdx === i ? 'border-navy bg-navy/10 dark:border-steel' : 'border-steel/30'}`}
                onClick={() => setWIdx(i)}
              >
                <span className="rounded-full bg-charcoal dark:bg-mist" style={{ width: 5 + i * 5, height: 5 + i * 5 }} />
              </button>
            ))}
          </div>
        </div>
        {penSeen && (
          <button type="button" className="w-full text-center text-xs text-steel underline" onClick={() => setFingers(!fingers)}>
            Apple Pencil detected – fingers {fingers ? 'can also draw' : "won't draw"} (tap to change)
          </button>
        )}
      </footer>

      {label && (
        <div className="fixed inset-x-0 top-0 z-50 space-y-3 bg-white p-4 pt-[max(1rem,env(safe-area-inset-top))] shadow-lg dark:bg-charcoal dark:text-mist">
          <label className="label" htmlFor="sk-label">{label.label ? 'Edit label' : 'New label'}</label>
          <input
            id="sk-label" className="input" autoFocus autoComplete="off" autoCapitalize="none" maxLength={40} enterKeyHint="done"
            placeholder="e.g. 4.2 m" value={label.text}
            onChange={(e) => setLabel({ ...label, text: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && finishLabel(false)}
          />
          <div className="flex gap-2">
            {UNITS.map((u) => (
              <button key={u} type="button" className="flex-1 rounded-lg border border-steel/40 py-2" onClick={() => setLabel({ ...label, text: `${label.text.replace(/\s+$/, '')} ${u}`.trim() })}>{u}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-primary flex-1 !py-3" onClick={() => finishLabel(false)}>Done</button>
            <button type="button" className="btn-ghost flex-1 !py-3" onClick={() => setLabel(null)}>Cancel</button>
            {label.label && <button type="button" className="btn-ghost !border-ascred/50 !py-3 !text-ascred" onClick={() => finishLabel(true)}>Delete</button>}
          </div>
        </div>
      )}

      {saveOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <div className="w-full max-w-md space-y-4 rounded-2xl bg-white p-5 dark:bg-charcoal dark:text-mist">
            <h2 className="text-xl font-bold">Save this sketch</h2>
            <p className="text-sm text-steel">{job.name}{job.jobNumber ? ` · ${job.jobNumber}` : ''}</p>
            <div>
              <label className="label" htmlFor="sk-cap">Comment (optional)</label>
              <textarea id="sk-cap" className="input" rows={2} maxLength={500} placeholder="e.g. Wash bay – existing layout" value={caption} onChange={(e) => setCaption(e.target.value)} />
            </div>
            <button type="button" className="btn-primary w-full" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save to job'}</button>
            <button type="button" className="btn-ghost w-full" disabled={saving} onClick={() => setSaveOpen(false)}>Keep drawing</button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default function Sketch() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const edit = sp.get('edit');
  const { status, job } = useJob(id);
  if (status === 'loading') return <div className="flex justify-center py-16"><Spinner className="h-10 w-10" /></div>;
  if (status !== 'ok') {
    return (
      <div className="space-y-4">
        <ErrorBox>{status === 'missing' ? "That job isn't available." : "Couldn't load the job. Check your signal and try again."}</ErrorBox>
        <Link to="/" className="btn-ghost w-full">Choose a job</Link>
      </div>
    );
  }
  return <Editor key={`${job.id}:${edit || 'new'}`} job={job} editSkid={/^[a-f0-9]{8}$/.test(edit || '') ? edit : null} />;
}
